import { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import FooterDesktop from './FooterDesktop';
import FooterMobile from './FooterMobile';
import footerSeoLinksData from '../data/footerSeoLinks.json';
import { FooterSeoLinks } from './FooterSeoLinks';
import { useAppDispatch } from '@/app/store/store';
import { setFooterInViewport } from '@/app/store/footerVisibilitySlice';

type FooterSeoLinkItem = { label: string; href: string };
type FooterSeoLinkSection = { title: string; items: FooterSeoLinkItem[] };

const footerSeoLinks = footerSeoLinksData as FooterSeoLinkSection[];

export default function Footer() {
  const dispatch = useAppDispatch();
  const rootRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const isDesktop = useIsDesktop();

  // Ensure we only render after hydration to prevent mismatch
  // Use useLayoutEffect for synchronous state updates during hydration
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Required for SSR hydration
    setMounted(true);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        dispatch(setFooterInViewport(entry?.isIntersecting ?? false));
      },
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      dispatch(setFooterInViewport(false));
    };
  }, [dispatch]);

  // During SSR and initial render, always render mobile to match server
  // After hydration, switch to the correct component
  const shouldRenderDesktop = mounted && isDesktop;

  return (
    <div ref={rootRef}>
      {shouldRenderDesktop ? <FooterDesktop /> : <FooterMobile />}
      <FooterSeoLinks sections={footerSeoLinks} />
    </div>
  );
}

